import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { config } from "@/lib/config";
import { supabase } from "@/integrations/supabase/client";

interface ReminderConfig {
  day_of_week: number;
  reminder_time: string;
  is_active?: boolean;
}

interface CreateTopicOptions {
  skipNavigation?: boolean;
  onClose?: () => void;
}

export const useCreateTopic = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: { 
      title: string; 
      studyGoalHours?: number; 
      reminders?: Array<{ day_of_week: number; reminder_time: string; is_active?: boolean }>;
      skipNavigation?: boolean;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      console.log('🚀 Creating topic with memory corpus integration:', {
        title: data.title,
        studyGoalHours: data.studyGoalHours || 5,
        remindersCount: data.reminders?.length || 0
      });

      // Get auth token for backend API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication token not available');
      }

      // Call enhanced backend API endpoint
      const response = await fetch(`${config.BACKEND_API_URL}/api/topics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          title: data.title,
          study_goal_hours: data.studyGoalHours || 5,
          reminders: data.reminders ?? []
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Topic creation failed:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.topic) {
        throw new Error('Invalid response from topic creation API');
      }

      const topic = result.topic;
      
      console.log('✅ Topic created successfully with enhanced backend:', {
        topicId: topic.topic_id,
        sessionId: topic.session_id,
        studyNoteId: topic.study_note_id,
        memoryCorpusCreated: topic.memory_corpus_created,
        memoryCorpusId: topic.memory_corpus_id
      });

      return {
        topicId: topic.topic_id,
        sessionId: topic.session_id,
        studyNoteId: topic.study_note_id,
        title: data.title,
        memoryCorpusCreated: topic.memory_corpus_created,
        memoryCorpusId: topic.memory_corpus_id
      };
    },
    onSuccess: (result, variables) => {
      console.log('🎉 Topic creation completed, invalidating queries...');
      
      // Invalidate multiple queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['topics'] });
      queryClient.invalidateQueries({ queryKey: ['topic-sessions', result.topicId] });
      queryClient.invalidateQueries({ queryKey: ['topic-notes', result.topicId] });

      // Memory corpus logging (toast notifications removed per user request)
      if (result.memoryCorpusCreated) {
        console.log('🧠 Memory corpus created for user');
      }

      // Navigate to the new topic with the created study note (unless skipNavigation is true)
      if (!variables.skipNavigation) {
        navigate(`/?topic=${result.topicId}&session=${result.sessionId}&note=${result.studyNoteId}`);
      }
    },
    onError: (error) => {
      console.error('💥 Topic creation failed:', error);
      
      toast({
        title: "Failed to create topic",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
    
    // Disable retries to prevent issues similar to study note creation
    retry: false,
  });

  // Expose the interface that components expect
  const createTopic = (
    title = "Untitled", 
    studyGoalHours = 5,
    reminders: ReminderConfig[] = [],
    options?: CreateTopicOptions
  ) => {
    return mutation.mutateAsync({
      title,
      studyGoalHours,
      reminders,
      skipNavigation: options?.skipNavigation
    }).then((result) => {
      options?.onClose?.();
      return { id: result.topicId, sessionId: result.sessionId };
    });
  };

  return {
    createTopic,
    isCreating: mutation.isPending,
    mutation
  };
};